import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Field = styled.input`
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 10px;
  outline: none;
  background-color: ${(props) => (props.disabled ? '#f5f5f5' : 'white')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'text')};
  color: #002e3b;
`;

const List = styled.ul`
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 4px;
  list-style: none;
  max-height: 240px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Item = styled.li`
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${(props) => (props.$active ? '#fff3e0' : 'transparent')};

  &:hover {
    background-color: #fff3e0;
  }
`;

const ItemLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #002e3b;
`;

const ItemDescricao = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const Empty = styled.div`
  padding: 8px 10px;
  font-size: 13px;
  color: #6b7280;
`;

const normalizar = (texto) =>
  (texto || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const ComboBox = ({
  options,
  value = '',
  onChange,
  placeholder = 'Selecione',
  disabled = false,
  maxResults = 50,
  acaoVazia = null,
}) => {
  const [query, setQuery] = useState('');
  const [aberto, setAberto] = useState(false);
  const blurTimeout = useRef(null);

  useEffect(() => () => clearTimeout(blurTimeout.current), []);

  const selecionado = useMemo(
    () => options.find((opt) => opt.value === value) || null,
    [options, value]
  );

  const filtrados = useMemo(() => {
    const termo = normalizar(query);
    const base = termo
      ? options.filter((opt) =>
          normalizar(`${opt.label} ${opt.descricao || ''} ${opt.busca || ''}`).includes(termo)
        )
      : options;
    return base.slice(0, maxResults);
  }, [options, query, maxResults]);

  const selecionar = (opt) => {
    onChange(opt.value);
    setQuery('');
    setAberto(false);
  };

  const valorExibido = aberto ? query : selecionado ? selecionado.label : '';

  return (
    <Wrapper>
      <Field
        type="text"
        value={valorExibido}
        placeholder={aberto && selecionado ? selecionado.label : placeholder}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!aberto) setAberto(true);
        }}
        onFocus={() => setAberto(true)}
        onBlur={() => {
          blurTimeout.current = setTimeout(() => setAberto(false), 120);
        }}
      />
      {aberto && (
        <List
          onMouseDown={() => clearTimeout(blurTimeout.current)}
        >
          {filtrados.length === 0 ? (
            <Empty>
              Nenhum resultado encontrado
              {acaoVazia}
            </Empty>
          ) : (
            filtrados.map((opt) => (
              <Item
                key={opt.value}
                $active={opt.value === value}
                onMouseDown={() => selecionar(opt)}
              >
                <ItemLabel>{opt.label}</ItemLabel>
                {opt.descricao && <ItemDescricao>{opt.descricao}</ItemDescricao>}
              </Item>
            ))
          )}
        </List>
      )}
    </Wrapper>
  );
};

ComboBox.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      descricao: PropTypes.string,
      busca: PropTypes.string,
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  maxResults: PropTypes.number,
  acaoVazia: PropTypes.node,
};

export default ComboBox;
